import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import {
  coerceHttpParams,
  QueryParams,
} from 'src/app/shared/flight-log-api/shared/create-request-options';
import { AppState } from 'src/store';
import {
  DeleteDutyLog,
  DutyLogEdited,
  DutyOff,
  DutyOn,
  UpdateDutyLog,
  UpsertDutyLog,
  UpsertDutyLogs,
} from 'src/store/duty-log/duty-log.actions';
import {
  CreateDutyLogParams,
  DutyLogEntity,
  UpdateDutyLogParams,
} from 'src/store/duty-log/duty-log.model';
import { tryGetEndOfDayISO, tryGetStartOfDayISO } from 'src/util/dateLib';
import { createHttpParams } from 'src/util/url';
import { assertNotNullOrWhitespace } from 'src/util/values';
import { DutyLogEntryViewModel } from 'src/view-models/duty-log-entry-view-model';
import { FLIGHT_LOG_API_CONFIG, FlightLogApiConfiguration } from '../flight-log-api-configuration';
import { CollectionResult } from '../flight-logs/flight-log-client-model-helpers';

export interface DutyLogQueryParams {
  pilotEmployeeId?: string;
  // filter duty logs to those with no end time
  active?: boolean;
  dutyOnStartTime?: Date;
  dutyOnEndTime?: Date;
  dutyOffStartTime?: Date;
  dutyOffEndTime?: Date;
  // get most recent duty log entry
  latest?: boolean;
  includeDeleted?: boolean;
  includeDispatched?: boolean;
  baseId?: string;
  companyId?: string;
  skip?: number;
  limit?: number;
}

export interface DutyOnParams {
  employeeId: string;
  baseId: string;
  aircraftId: string;
  dutyPeriodStartTime: Date;
  comments?: string;
}

export interface DutyOffParams extends DutyOnParams {
  id: string;
  dutyPeriodEndTime: Date;
  justification: string;
}

export interface DutyLogs<T extends DutyLogEntity = DutyLogEntity> extends CollectionResult<T> {
  isOnDuty: boolean;
}

@Injectable({ providedIn: 'root' })
export class DutyLogHttpClient {
  constructor(
    @Inject(FLIGHT_LOG_API_CONFIG) private config: FlightLogApiConfiguration,
    private _http: HttpClient,
    private _store: Store<AppState>,
  ) {}

  public getById(dutyLogEntryId: string): Observable<DutyLogEntity | undefined> {
    assertNotNullOrWhitespace(dutyLogEntryId, 'dutyLogEntryId');
    return this._http
      .get<{ results: DutyLogEntity[] }>(this._url(`dutyLogs/${dutyLogEntryId}`))
      .pipe(
        map((res) => res.results[0] as DutyLogEntity | undefined),
        tap((dl) => {
          this._store.dispatch(
            dl != null
              ? new UpsertDutyLog({ dutyLog: dl })
              : new DeleteDutyLog({ id: dutyLogEntryId }),
          );
        }),
      );
  }

  public getMany(params: DutyLogQueryParams): Observable<DutyLogs<DutyLogEntryViewModel>> {
    const queryParams: QueryParams<DutyLogQueryParams> = {
      baseId: params.baseId,
      includeDeleted: params.includeDeleted != null ? String(params.includeDeleted) : undefined,
      includeDispatched:
        params.includeDispatched != null ? String(params.includeDispatched) : undefined,
      latest: params.latest != null ? String(params.latest) : undefined,
      active: params.active != null ? String(params.active) : undefined,
      pilotEmployeeId: params.pilotEmployeeId,
      companyId: params.companyId,
      dutyOffStartTime: tryGetStartOfDayISO(params.dutyOffStartTime),
      dutyOffEndTime: tryGetEndOfDayISO(params.dutyOffEndTime),
      dutyOnStartTime: tryGetStartOfDayISO(params.dutyOnStartTime),
      dutyOnEndTime: tryGetEndOfDayISO(params.dutyOnEndTime),
      skip: params.skip != null ? String(params.skip) : undefined,
      limit: params.limit != null ? String(params.limit) : undefined,
    };
    return this._http
      .get<DutyLogs>(this._url(`dutyLogs`), {
        params: createHttpParams(queryParams),
      })
      .pipe(
        tap(({ results: dutyLogs }) => {
          this._store.dispatch(new UpsertDutyLogs({ dutyLogs }));
        }),
        map((res) => {
          return {
            results: res.results.map((r) => new DutyLogEntryViewModel(r)),
            totalCount: res.totalCount,
            isOnDuty: res.isOnDuty,
          };
        }),
      );
  }

  public dutyOn(dutyOn: DutyOnParams): Observable<any> {
    return this._http.post(this._url(`dutyLogs/dutyOn`), dutyOn).pipe(
      tap(() => {
        this._store.dispatch(new DutyOn(dutyOn));
      }),
    );
  }

  public create(dutyLogEntry: CreateDutyLogParams): Observable<any> {
    // an empty value will create a duty log entry with an empty string for an ID,
    // we're guarding against that by ensuring the id is undefined
    delete (dutyLogEntry as any).id;
    return this._http.post(this._url('dutyLogs'), dutyLogEntry);
  }

  public dutyOff(dutyOff: DutyOffParams): Observable<any> {
    assertNotNullOrWhitespace(dutyOff.id, 'dutyLogEntry.id');
    return this._http.post(this._url(`dutyLogs/dutyOff`), dutyOff).pipe(
      tap(() => {
        this._store.dispatch(new DutyOff(dutyOff));
        this._store.dispatch(
          new UpdateDutyLog({
            dutyLog: {
              id: dutyOff.id,
              changes: dutyOff,
            },
          }),
        );
      }),
    );
  }

  public update(dutyLogEntry: UpdateDutyLogParams): Observable<any> {
    assertNotNullOrWhitespace(dutyLogEntry.id, 'dutyLogEntry.id');
    return this._http.put(this._url(`dutyLogs/${dutyLogEntry.id}`), dutyLogEntry).pipe(
      tap(() => {
        this._store.dispatch(new UpsertDutyLog({ dutyLog: dutyLogEntry }));
        this._store.dispatch(new DutyLogEdited(dutyLogEntry));
        this._store.dispatch(
          new UpdateDutyLog({
            dutyLog: {
              id: dutyLogEntry.id,
              changes: dutyLogEntry,
            },
          }),
        );
      }),
    );
  }

  public delete({ id, pilotEmployeeId }: { id: string; pilotEmployeeId: string }): Observable<any> {
    return this._http
      .delete(this._url(`dutyLogs/${id}`), {
        params: coerceHttpParams({ pilotEmployeeId }),
      })
      .pipe(
        tap(() => {
          this._store.dispatch(new DeleteDutyLog({ id }));
        }),
      );
  }

  private _url(path: string) {
    return this.config.baseUri + '/api/' + path;
  }
}
